import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Paper,
  PasswordInput,
  Button,
  Text,
  Container,
  Stack,
  Center,
  useMantineColorScheme,
  Box,
  Image,
  ActionIcon,
  Alert,
  Progress,
  Group
} from '@mantine/core';
import { IconArrowLeft, IconLock, IconCheck, IconAlertCircle, IconX, IconLoader } from '@tabler/icons-react';
import authService from '../services/authService';
import enarmLogo from '../assets/enarm_logo.png';
import { getGlassmorphismInputStyles, glassmorphismInputProps } from '../styles/inputStyles';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');

  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    if (!token) {
      setError('Token no proporcionado');
      setValidatingToken(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await authService.validateResetToken(token!);

      if (response.valid) {
        setTokenValid(true);
        setEmail(response.email || '');
      } else {
        setTokenValid(false);
        setError(response.message || 'El enlace de recuperación es inválido o ha expirado');
      }
    } catch (err) {
      console.error('Error validando token:', err);
      setTokenValid(false);
      setError('Error al validar el token');
    } finally {
      setValidatingToken(false);
    }
  };

  const getPasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 6) strength += 25;
    if (password.length >= 8) strength += 25;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
    if (/\d/.test(password)) strength += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 10;
    return Math.min(strength, 100);
  };

  const getPasswordStrengthColor = (strength: number): string => {
    if (strength < 40) return 'red';
    if (strength < 70) return 'yellow';
    return 'green';
  };

  const passwordStrength = getPasswordStrength(newPassword);

  // Verificar si las contraseñas coinciden
  const passwordsMatch = confirmPassword.length > 0 && newPassword === confirmPassword;
  const passwordsDontMatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await authService.resetPassword(token!, newPassword, confirmPassword);

      if (response.success) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(response.message || 'Error al restablecer la contraseña');
      }
    } catch (err: any) {
      console.error('Error en reset password:', err);
      setError(err.response?.data?.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <Box
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: colorScheme === 'dark'
            ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
            : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        }}
      >
        <Stack align="center" gap="md">
          <IconLoader size={48} style={{ animation: 'spin 1s linear infinite' }} />
          <Text size="lg" c="dimmed">Validando enlace...</Text>
        </Stack>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </Box>
    );
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        width: '100vw',
        background: colorScheme === 'dark'
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
          : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Textura de fondo */}
      <Box
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: colorScheme === 'dark'
            ? `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23374151" stroke-width="0.5" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`
            : `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><path d="M 20 0 L 0 0 0 20" fill="none" stroke="%23ddd8d1" stroke-width="0.5" opacity="0.3"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>')`,
          opacity: 0.5,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Back Button */}
      <ActionIcon
        onClick={() => navigate('/login')}
        variant="light"
        size="lg"
        radius="xl"
        style={{
          position: 'absolute',
          top: '2rem',
          left: '2rem',
          zIndex: 10,
          backgroundColor: colorScheme === 'dark'
            ? 'rgba(30, 41, 59, 0.8)'
            : 'rgba(247, 243, 238, 0.8)',
          backdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
        }}
      >
        <IconArrowLeft size={20} />
      </ActionIcon>

      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1
      }}>
        <Container size={460} style={{ width: '100%', maxWidth: '460px' }}>
          <Paper
            radius="xl"
            p="lg"
            withBorder
            style={{
              width: '100%',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(247, 243, 238, 0.6)',
              backdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
              boxShadow: colorScheme === 'dark'
                ? 'inset 0 1px 0 rgba(255, 255, 255, 0.1), 0 4px 20px rgba(0, 0, 0, 0.3)'
                : '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
            }}
          >
            <Stack gap="lg">
              <Center>
                <Stack align="center" gap="sm">
                  <Image
                    src={enarmLogo}
                    alt="ENARM360 Logo"
                    height={65}
                    fit="contain"
                    style={{ borderRadius: '12px', maxWidth: '200px' }}
                  />
                  <Text size="xl" fw={700} ta="center" style={{ color: colorScheme === 'dark' ? '#ffffff' : '#1e293b' }}>
                    Restablecer contraseña
                  </Text>
                  {email && (
                    <Text size="sm" c="dimmed" ta="center">
                      Para: {email}
                    </Text>
                  )}
                </Stack>
              </Center>

              {!tokenValid ? (
                <Alert
                  icon={<IconX size={16} />}
                  color="red"
                  radius="xl"
                  variant="light"
                  style={{
                    backgroundColor: colorScheme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: `1px solid rgba(239, 68, 68, 0.2)`,
                  }}
                >
                  <Stack gap="xs">
                    <Text fw={600}>Enlace inválido o expirado</Text>
                    <Text size="sm">{error}</Text>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => navigate('/forgot-password')}
                      mt="xs"
                    >
                      Solicitar nuevo enlace
                    </Button>
                  </Stack>
                </Alert>
              ) : !success ? (
                <form onSubmit={handleSubmit}>
                  <Stack gap="md">
                    <PasswordInput
                      label="Nueva contraseña"
                      placeholder="Ingresa tu nueva contraseña"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={loading}
                      leftSection={<IconLock size={16} />}
                      {...glassmorphismInputProps}
                      styles={getGlassmorphismInputStyles(colorScheme)}
                    />

                    {newPassword && (
                      <Box>
                        <Group justify="space-between" mb={5}>
                          <Text size="xs" c="dimmed">Fortaleza de la contraseña</Text>
                          <Text size="xs" c="dimmed">{passwordStrength}%</Text>
                        </Group>
                        <Progress value={passwordStrength} color={getPasswordStrengthColor(passwordStrength)} size="sm" />
                      </Box>
                    )}

                    <Box>
                      <PasswordInput
                        label="Confirmar contraseña"
                        placeholder="Confirma tu nueva contraseña"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={loading}
                        leftSection={<IconLock size={16} />}
                        error={passwordsDontMatch ? "Las contraseñas no coinciden" : undefined}
                        {...glassmorphismInputProps}
                        styles={getGlassmorphismInputStyles(colorScheme)}
                      />
                      {passwordsMatch && (
                        <Text size="xs" c="green" mt={5}>
                          <Group gap={4}>
                            <IconCheck size={14} />
                            Las contraseñas coinciden
                          </Group>
                        </Text>
                      )}
                    </Box>

                    {error && (
                      <Alert
                        icon={<IconAlertCircle size={16} />}
                        color="red"
                        radius="xl"
                        variant="light"
                        style={{
                          backgroundColor: colorScheme === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
                          backdropFilter: 'blur(20px) saturate(180%)',
                          border: `1px solid rgba(239, 68, 68, 0.2)`,
                        }}
                      >
                        {error}
                      </Alert>
                    )}

                    <Button
                      type="submit"
                      fullWidth
                      loading={loading}
                      disabled={!newPassword || !confirmPassword || newPassword.length < 6}
                      size="md"
                      radius="xl"
                      variant="light"
                      style={{
                        marginTop: '0.5rem',
                        backgroundColor: colorScheme === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(247, 243, 238, 0.8)',
                        backdropFilter: 'blur(40px) saturate(200%)',
                        border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(221, 216, 209, 0.8)'}`,
                        color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                        fontFamily: 'Space Grotesk, Inter, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {loading ? 'Actualizando...' : 'Restablecer contraseña'}
                    </Button>
                  </Stack>
                </form>
              ) : (
                <Alert
                  icon={<IconCheck size={16} />}
                  color="green"
                  radius="xl"
                  variant="light"
                  style={{
                    backgroundColor: colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)',
                    backdropFilter: 'blur(20px) saturate(180%)',
                    border: `1px solid rgba(34, 197, 94, 0.2)`,
                  }}
                >
                  <Stack gap="xs">
                    <Text fw={600}>Contraseña actualizada exitosamente</Text>
                    <Text size="sm">
                      Tu contraseña ha sido restablecida. Serás redirigido al inicio de sesión en unos segundos...
                    </Text>
                  </Stack>
                </Alert>
              )}
            </Stack>
          </Paper>
        </Container>
      </div>
    </Box>
  );
};

export default ResetPasswordPage;
